import { createAiClient, type ChatRequest } from '@fim/ai-sdk';
import { sanitizeAiError, type ChatTurn, type FutureXClient, type StreamEvent } from '@fim/futurex-protocol';

/**
 * Phase-0 backend: streams straight from DeepSeek via `@fim/ai-sdk`, mapping its
 * `ChatChunk`s onto the protocol's `StreamEvent`s. In Phase 1 a gateway-backed
 * client implements the same {@link FutureXClient} and this is swapped out — the
 * REPL/ask code never changes.
 *
 * NOTE: this is the ONLY place the CLI talks to DeepSeek directly, and the
 * `@fim/ai-sdk` dependency is **Phase-0 only**. The end-state CLI (per the
 * review / §16) routes through the gateway and never sees the model — so this
 * file AND the `@fim/ai-sdk` dependency in package.json must be deleted when the
 * gateway-backed client lands in Phase 1. See docs/futurex/review.md (reuse map).
 */
export function createDirectClient(): FutureXClient {
  const ai = createAiClient({ provider: 'deepseek' });

  return {
    async *chat(turn: ChatTurn): AsyncIterable<StreamEvent> {
      const request: ChatRequest = {
        messages: turn.messages.map((m) => ({ role: m.role, content: m.content })),
        system: turn.system,
        reasoning: turn.reasoning,
        model: turn.model,
        maxTokens: turn.maxTokens,
        signal: turn.signal,
      };

      try {
        for await (const chunk of ai.stream(request)) {
          if (chunk.reasoningDelta) {
            yield { t: 'thought', text: chunk.reasoningDelta };
          }
          if (chunk.delta) {
            yield { t: 'token', text: chunk.delta };
          }
          if (chunk.done) {
            if (chunk.usage) {
              yield {
                t: 'usage',
                usage: {
                  inputTokens: chunk.usage.inputTokens,
                  outputTokens: chunk.usage.outputTokens,
                  totalTokens: chunk.usage.totalTokens,
                  ...(chunk.usage.cacheHitTokens !== undefined
                    ? { cacheHitTokens: chunk.usage.cacheHitTokens }
                    : {}),
                  ...(chunk.usage.cacheMissTokens !== undefined
                    ? { cacheMissTokens: chunk.usage.cacheMissTokens }
                    : {}),
                  ...(chunk.usage.reasoningTokens !== undefined
                    ? { reasoningTokens: chunk.usage.reasoningTokens }
                    : {}),
                },
              };
            }
            yield { t: 'done', finishReason: chunk.finishReason };
          }
        }
      } catch (err) {
        yield { t: 'error', message: sanitizeAiError(err) };
      }
    },
  };
}
